import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ID } from '../../../../types/aiTypes';

type Props = {
  servers: any[];
  loading?: boolean;
  selectedServerId: ID | null;
  onSelect: (id: ID) => void;
  onAdd: () => void;
  onEdit: (server: any) => void;
  onDelete: (id: ID) => void;
  onRefresh: () => void;
};

const statusColor = (status?: string): 'success' | 'warning' | 'error' | 'default' => {
  const s = String(status || '').toLowerCase();
  if (s === 'healthy') return 'success';
  if (s === 'degraded') return 'warning';
  if (s === 'unhealthy' || s === 'failed') return 'error';
  return 'default';
};

const MCPServerListPanel: React.FC<Props> = ({
                                               servers,
                                               loading,
                                               selectedServerId,
                                               onSelect,
                                               onAdd,
                                               onEdit,
                                               onDelete,
                                               onRefresh,
                                             }) => {
  const hasServers = servers.length > 0;
  const showInitialLoading = !!loading && !hasServers;
  const showRefreshing = !!loading && hasServers;

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">MCP Servers</Typography>

            {showRefreshing ? (
              <Chip
                size="small"
                variant="outlined"
                label="Refreshing..."
              />
            ) : null}
          </Stack>

          <Typography variant="caption" color="text.secondary">
            Registry, health, discovery, and capability inventory
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            disabled={showInitialLoading}
          >
            Refresh
          </Button>

          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={onAdd}
          >
            Add
          </Button>
        </Stack>
      </Stack>

      {showInitialLoading ? (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading servers...
          </Typography>
        </Box>
      ) : null}

      <List dense sx={{ maxHeight: 780, overflow: 'auto', p: 0 }}>
        {servers.map((server) => {
          const selected = String(selectedServerId) === String(server.id);

          return (
            <ListItemButton
              key={String(server.id)}
              selected={selected}
              onClick={() => onSelect(server.id)}
              sx={{
                mb: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: selected ? 'primary.main' : 'divider',
                alignItems: 'stretch',
                p: 1.5,
                transition: 'border-color 120ms ease, background-color 120ms ease',
              }}
            >
              <Box sx={{ width: '100%', minWidth: 0 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                      {server.name}
                    </Typography>

                    <Typography variant="caption" color="text.secondary" display="block">
                      {server.slug}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ wordBreak: 'break-word' }}
                    >
                      {server.base_url}
                    </Typography>
                  </Box>

                  <Stack spacing={1} alignItems="flex-end">
                    <Chip
                      size="small"
                      color={statusColor(server.health_status)}
                      label={String(server.health_status || 'unknown')}
                    />

                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          onEdit(server);
                        }}
                      >
                        Edit
                      </Button>

                      <Button
                        size="small"
                        color="error"
                        onClick={(event) => {
                          event.stopPropagation();

                          if (window.confirm(`Delete MCP server "${server.name || server.slug}"?`)) {
                            onDelete(server.id);
                          }
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>

                <Stack direction="row" spacing={2} mt={1} flexWrap="wrap" useFlexGap>
                  <Typography variant="caption">
                    Configured: <b>{server.configured_count ?? 0}</b>
                  </Typography>

                  <Typography variant="caption">
                    Discovered: <b>{server.discovered_count ?? 0}</b>
                  </Typography>

                  <Typography variant="caption">
                    Priority: <b>{server.priority ?? '-'}</b>
                  </Typography>
                </Stack>
              </Box>
            </ListItemButton>
          );
        })}

        {!servers.length && !loading ? (
          <Typography variant="body2" color="text.secondary">
            No MCP servers found.
          </Typography>
        ) : null}
      </List>
    </Paper>
  );
};

export default MCPServerListPanel;